/**
 * API Client
 * 
 * Provides utilities for making authenticated and public API requests.
 * Handles JWT token injection for protected endpoints.
 * 
 * Requirements: 4.6, 4.7
 */

import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { getSession, createAuthHeader } from '../utils/auth';

// API base URLs
const WP_API_URL = import.meta.env.VITE_WP_API_URL || '';
const WKS_API_BASE = `${WP_API_URL}/wp-json/wks-product-entry/v1`;
const YMM_API_BASE = `${WP_API_URL}/wp-json/ymm-search/v1`;

/**
 * List of protected WKS API endpoints that require JWT authentication
 */
export const PROTECTED_ENDPOINTS = [
  '/sku/check-internal',
  '/sku/generate',
  '/sku/options',
  '/categories',
  '/brands',
  '/attributes',
  '/products',
  '/media',
];

/**
 * List of public YMM Search API endpoints that don't require authentication
 */
export const PUBLIC_ENDPOINTS = [
  '/sku/',
  '/product/',
];

/**
 * Check if an endpoint is a protected WKS endpoint
 */
export function isProtectedEndpoint(url: string): boolean {
  return PROTECTED_ENDPOINTS.some(endpoint => url.includes(endpoint));
}

/**
 * Check if an endpoint is a public YMM endpoint
 */
export function isPublicEndpoint(url: string): boolean {
  return PUBLIC_ENDPOINTS.some(endpoint => url.includes(endpoint));
}

/**
 * Create request config with auth header for protected endpoints
 * 
 * @param config - Base request config
 * @returns Config with Authorization header added
 */
export function createProtectedRequestConfig(config: AxiosRequestConfig = {}): AxiosRequestConfig {
  const session = getSession();
  
  if (!session?.token) {
    throw new Error('No authentication token available');
  }

  return {
    ...config,
    headers: {
      ...config.headers,
      Authorization: createAuthHeader(session.token),
    },
  };
}

/**
 * Create request config for public endpoints (no auth header)
 * 
 * @param config - Base request config
 * @returns Config without Authorization header
 */
export function createPublicRequestConfig(config: AxiosRequestConfig = {}): AxiosRequestConfig {
  // Explicitly exclude Authorization header for public endpoints
  const { Authorization, ...otherHeaders } = (config.headers || {}) as Record<string, string>;
  
  return {
    ...config,
    headers: otherHeaders,
  };
}

/**
 * Create an axios instance for protected WKS API requests
 * Automatically adds JWT token to all requests
 */
export function createProtectedClient(): AxiosInstance {
  const client = axios.create({
    baseURL: WKS_API_BASE,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add auth interceptor
  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const session = getSession();
    
    if (session?.token) {
      config.headers.Authorization = createAuthHeader(session.token);
    }
    
    return config;
  });

  return client;
}

/**
 * Create an axios instance for public YMM API requests
 * Does NOT add authentication headers
 */
export function createPublicClient(): AxiosInstance {
  const client = axios.create({
    baseURL: YMM_API_BASE,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Ensure no auth headers are added
  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    // Remove any Authorization header that might have been added
    delete config.headers.Authorization;
    return config;
  });

  return client;
}

// Singleton instances
let protectedClient: AxiosInstance | null = null;
let publicClient: AxiosInstance | null = null;

/**
 * Get the protected API client (singleton)
 */
export function getProtectedClient(): AxiosInstance {
  if (!protectedClient) {
    protectedClient = createProtectedClient();
  }
  return protectedClient;
}

/**
 * Get the public API client (singleton)
 */
export function getPublicClient(): AxiosInstance {
  if (!publicClient) {
    publicClient = createPublicClient();
  }
  return publicClient;
}

/**
 * Reset clients (useful for testing)
 */
export function resetClients(): void {
  protectedClient = null;
  publicClient = null;
}
